'use client';
import React, { useEffect, useState } from 'react';
import { BlocksTabChange } from 'blocks-nextjs/blocks-web-components/events';
import {
  SELECTED_EQUIPMENT,
  TAB_INDEX_SERIAL
} from 'components/my-equipment/constants';
import { useTranslations } from 'next-intl';
import {
  Conditional,
  MyEquipmentButton,
  MyEquipmentTabs
} from 'components/pcc-components';
import { CatCard } from 'components/blocks-components';
import { AddEquipmentBySerial } from 'components/my-equipment/add-equipment/add-equipment-form/add-equipment-by-serial/add-equipment-by-serial';
import { AddEquipmentByModel } from 'components/my-equipment/add-equipment/add-equipment-form/add-equipment-by-model/add-equipment-by-model';
import { useFormContext } from 'react-hook-form';
import { useFormStatus } from 'react-dom';
import { useFeedback } from 'providers/feedback-provider';
import { ErrorBannerMessage } from 'components/my-equipment/error-banner-message';
import { selectEquipment } from 'services/equipment/actions';
import { UNKNOWN_ERROR_MESSAGE } from 'constants/logging';
import { useQuickToast } from 'hooks/use-toast';
import { capitalizeText } from '@cat-ecom/common/utils/stringUtils';
import { usePartDiagram } from 'hooks/use-part-diagram/parts-diagram-context';

const EquipmentTabs = ({ formLocation }: { formLocation: string }) => {
  const form = useFormContext();
  const watchSelectedEquipment = form?.watch(SELECTED_EQUIPMENT);
  const t = useTranslations();
  const [activeIndex, setActiveIndex] = useState(TAB_INDEX_SERIAL);
  const [
    showSearchSuggestionFailureBanner,
    setShowSearchSuggestionFailureBanner
  ] = useState(false);
  const [disableSaveButton, setDisableSaveButton] = useState(true);
  const { dismissFeedback, showFeedback, feedbackMessage } = useFeedback();
  const { setEquipmentError } = usePartDiagram();
  const showErrorBanner = showSearchSuggestionFailureBanner;
  const toast = useQuickToast();

  useEffect(() => {
    if (showFeedback && feedbackMessage === t(UNKNOWN_ERROR_MESSAGE)) {
      setEquipmentError(true);
    } else {
      setEquipmentError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFeedback, feedbackMessage]);

  const { pending: isAddEquipmentFormSubmitting } = useFormStatus();
  const onTabChange = (e: BlocksTabChange) => {
    setActiveIndex(e.detail.activeTabIndex);
    form.reset();
    setShowSearchSuggestionFailureBanner(false);
    dismissFeedback();
  };
  const showAddEquipmentToast = (selectedEquipmentValues) => {
    const { model = '', equipmentFamily = '' } = selectedEquipmentValues || {};
    const currentlyShoppingText = t('CURRENTLY_SHOPPING', {
      0: model,
      1: capitalizeText(equipmentFamily)
    });
    toast.success({
      message: currentlyShoppingText,
      delay: 5
    });
  };

  const handleYesClickedDuplicateBanner = () => {
    dismissFeedback();
    showAddEquipmentToast(watchSelectedEquipment);
    if (watchSelectedEquipment != null) {
      const equipmentId =
        watchSelectedEquipment.productId || watchSelectedEquipment.partNumber;
      selectEquipment(equipmentId, watchSelectedEquipment?.serialNo);
    }
  };

  useEffect(() => {
    setDisableSaveButton(!watchSelectedEquipment);
  }, [watchSelectedEquipment]);

  return (
    <div className="cat-u-margin-top-md cat-u-margin-bottom-md">
      <CatCard>
        <Conditional test={showErrorBanner}>
          <ErrorBannerMessage
            buttonLabel={t('FOOTER_CONTACT_US')}
            bannerLabel={t('UNKNOWN_ERROR_MESSAGE')}
          />
        </Conditional>
        <MyEquipmentTabs
          activeIndex={activeIndex}
          onBlTabChange={onTabChange}
          behavior="full-width"
        >
          <AddEquipmentBySerial
            setShowSearchSuggestionFailureBanner={
              setShowSearchSuggestionFailureBanner
            }
            handleYesClickedDuplicateBanner={handleYesClickedDuplicateBanner}
            showBackButton={false}
            isChild={false}
            formLocation={formLocation}
          />
          <AddEquipmentByModel
            setShowSearchSuggestionFailureBanner={
              setShowSearchSuggestionFailureBanner
            }
            handleYesClickedDuplicateBanner={handleYesClickedDuplicateBanner}
            showBackButton={false}
            isChild={false}
            formLocation={formLocation}
          />
        </MyEquipmentTabs>
        <MyEquipmentButton
          type="submit"
          disabled={disableSaveButton || isAddEquipmentFormSubmitting}
        >
          {t('MSTLST_ADD')}
        </MyEquipmentButton>
      </CatCard>
    </div>
  );
};

export default EquipmentTabs;
